import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Activity, Database, Server, Shield, Mail, MessageSquare, RefreshCw, Loader2,
  CheckCircle2, XCircle, AlertTriangle, Send, Clock, Users, Building2, TrendingUp, Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { RELEASE_VERSION } from '@/data/releaseNotes';
import { useAuth } from '@/contexts/AuthContext';

function fmtUptime(seconds) {
  if (seconds == null) return '—';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function fmtTime(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function StatusDot({ ok, warn }) {
  if (warn) return <span className="inline-block w-2 h-2 rounded-full bg-amber-400" />;
  return <span className={`inline-block w-2 h-2 rounded-full ${ok ? 'bg-emerald-400' : 'bg-rose-400'}`} />;
}

function StatCard({ label, value, sublabel, icon: Icon, tone = 'default' }) {
  const toneMap = {
    default: 'bg-card border text-foreground',
    good:    'bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-300',
    warn:    'bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-300',
    bad:     'bg-rose-500/10 border-rose-500/30 text-rose-700 dark:text-rose-300',
    info:    'bg-blue-500/10 border-blue-500/30 text-blue-700 dark:text-blue-300',
  };
  return (
    <div className={`rounded-xl border p-4 ${toneMap[tone]}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider opacity-80">
        {Icon && <Icon className="w-3 h-3" />}
        {label}
      </div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
      {sublabel && <div className="text-[11px] opacity-70 mt-0.5">{sublabel}</div>}
    </div>
  );
}

function SectionHeader({ icon: Icon, title, rightSlot }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-blue-400" />
        <h2 className="text-sm font-heading font-semibold text-foreground">{title}</h2>
      </div>
      {rightSlot}
    </div>
  );
}

export default function DeployStatusPage() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState({ slack: false, email: false });

  const isSuperAdmin = user?.role === 'super_admin';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getDeployStatus();
      setStatus(data);
    } catch (err) {
      toast.error(err.message || 'Failed to load deploy status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isSuperAdmin) load(); else setLoading(false); }, [load, isSuperAdmin]);

  const runSlackTest = async () => {
    setTesting(t => ({ ...t, slack: true }));
    try {
      const res = await api.testDeployStatusSlack();
      if (res.success) toast.success(`Slack ping delivered via ${res.mode}${res.channel ? ` → ${res.channel}` : ''}`);
      else toast.error(res.message || res.error || 'Slack test failed');
    } catch (err) {
      toast.error(err.message || 'Slack test failed');
    } finally {
      setTesting(t => ({ ...t, slack: false }));
    }
  };

  const runEmailTest = async () => {
    setTesting(t => ({ ...t, email: true }));
    try {
      const res = await api.testDeployStatusEmail();
      if (res.success) toast.success(`Test email sent to ${res.to}`);
      else toast.error(res.error || 'Email test failed');
    } catch (err) {
      toast.error(err.message || 'Email test failed');
    } finally {
      setTesting(t => ({ ...t, email: false }));
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="p-8" data-testid="deploy-status-forbidden">
        <div className="max-w-md mx-auto rounded-2xl border border-amber-500/30 bg-amber-500/10 p-6 text-center">
          <AlertTriangle className="w-8 h-8 text-amber-400 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-foreground mb-1">Super admin only</h2>
          <p className="text-sm text-muted-foreground">Deploy status is restricted to super admins.</p>
        </div>
      </div>
    );
  }

  if (loading || !status) {
    return (
      <div className="p-8 text-center text-muted-foreground" data-testid="deploy-status-loading">
        <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" />Loading production health…
      </div>
    );
  }

  const s = status;
  const allGreen = s.services?.mongo?.ok && s.services?.backend?.ok && s.secrets?.JWT_SECRET?.present && s.secrets?.MONGO_URL?.present;
  const mongoTone = s.services?.mongo?.ok ? 'good' : 'bad';
  const totalTrialAtRisk = (s.tenants?.trial_expiring_soon || 0) + (s.tenants?.trial_expired || 0);

  return (
    <div className="p-6 space-y-6 max-w-6xl" data-testid="deploy-status-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-foreground flex items-center gap-2">
            <Activity className="w-6 h-6 text-blue-400" />
            Deploy Status
            <Badge className={`ml-2 text-[10px] ${allGreen ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30' : 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30'}`} data-testid="deploy-overall-badge">
              {allGreen ? 'All systems operational' : 'Needs attention'}
            </Badge>
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Live production health — services, secrets, tenant funnel and integrations. Generated {fmtTime(s.generated_at)}.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} className="rounded-xl" data-testid="refresh-deploy-btn">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Services */}
      <div className="rounded-2xl border border bg-card p-5">
        <SectionHeader icon={Server} title="Core Services" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="rounded-xl border border bg-card p-4" data-testid="svc-mongo">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground">
              <Database className="w-3 h-3" /> MongoDB
            </div>
            <div className="flex items-center gap-2 mt-1">
              <StatusDot ok={s.services.mongo.ok} />
              <span className="text-lg font-semibold text-foreground">{s.services.mongo.ok ? 'Connected' : 'Down'}</span>
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5">
              {s.services.mongo.ok ? `ping ${s.services.mongo.latency_ms} ms` : (s.services.mongo.error || 'unknown error')}
            </div>
          </div>

          <div className="rounded-xl border border bg-card p-4" data-testid="svc-backend">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground">
              <Server className="w-3 h-3" /> Backend
            </div>
            <div className="flex items-center gap-2 mt-1">
              <StatusDot ok={s.services.backend.ok} />
              <span className="text-lg font-semibold text-foreground">Running</span>
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5">
              uptime {fmtUptime(s.services.backend.uptime_seconds)} · booted {fmtTime(s.services.backend.started_at)}
            </div>
          </div>

          <div className="rounded-xl border border bg-card p-4">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground">
              <Zap className="w-3 h-3" /> Platform
            </div>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-lg font-semibold text-foreground">v{RELEASE_VERSION}</span>
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5">Tenants: {s.tenants.total} · Users: {s.users.total}</div>
          </div>
        </div>
      </div>

      {/* Secrets */}
      <div className="rounded-2xl border border bg-card p-5">
        <SectionHeader icon={Shield} title="Secrets & Environment" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="secrets-grid">
          {Object.entries(s.secrets).map(([name, meta]) => (
            <div key={name} className={`rounded-xl border p-3 ${meta.present ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-rose-500/10 border-rose-500/30'}`} data-testid={`secret-${name}`}>
              <div className="flex items-center gap-2">
                <StatusDot ok={meta.present} />
                <span className="text-[11px] font-mono text-foreground truncate">{name}</span>
              </div>
              <div className="text-[10px] text-muted-foreground mt-1">
                {meta.present
                  ? `${meta.length} chars set${meta.source && meta.source !== 'env' ? ` · from ${meta.source}` : ''}`
                  : 'not configured'}
              </div>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground mt-3">Only presence + length shown — values are never sent to the browser.</p>
      </div>

      {/* Tenants funnel */}
      <div className="rounded-2xl border border bg-card p-5">
        <SectionHeader icon={Building2} title="Tenant Funnel" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="tenant-funnel">
          <StatCard label="Total" value={s.tenants.total} sublabel={`incl. ${s.tenants.system} system`} icon={Building2} />
          <StatCard label="Trial — active" value={s.tenants.trial_active} icon={TrendingUp} tone="info" />
          <StatCard label="Trial — expiring ≤ 3d" value={s.tenants.trial_expiring_soon} icon={Clock} tone={s.tenants.trial_expiring_soon > 0 ? 'warn' : 'default'} />
          <StatCard label="Trial — expired" value={s.tenants.trial_expired} icon={XCircle} tone={s.tenants.trial_expired > 0 ? 'bad' : 'default'} />
          <StatCard label="Paid" value={s.tenants.paid} icon={CheckCircle2} tone="good" />
          <StatCard label="Pending approval" value={s.tenants.pending_approval} icon={Clock} tone={s.tenants.pending_approval > 0 ? 'warn' : 'default'} />
          <StatCard label="Users" value={s.users.total} sublabel={`${s.users.active_last_7d} active last 7d`} icon={Users} />
          <StatCard label="At-risk trials" value={totalTrialAtRisk} sublabel="expiring + expired" icon={AlertTriangle} tone={totalTrialAtRisk > 0 ? 'warn' : 'good'} />
        </div>
      </div>

      {/* Integrations */}
      <div className="rounded-2xl border border bg-card p-5">
        <SectionHeader icon={MessageSquare} title="Integrations" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="rounded-xl border border bg-card p-4" data-testid="integ-sendgrid">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium text-foreground">SendGrid (Email)</span>
                <StatusDot ok={s.integrations.system_sendgrid_configured} warn={!s.integrations.system_sendgrid_configured && s.integrations.sendgrid_tenants_configured > 0} />
              </div>
              <Button size="sm" onClick={runEmailTest} disabled={testing.email} className="rounded-lg bg-blue-500 hover:bg-blue-600 text-foreground" data-testid="test-email-btn">
                {testing.email ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                Test send
              </Button>
            </div>
            <div className="text-[11px] text-muted-foreground mt-2">
              System-level: <span className={s.integrations.system_sendgrid_configured ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'}>{s.integrations.system_sendgrid_configured ? 'configured' : 'not set'}</span>
              {' · '}Tenant overrides: {s.integrations.sendgrid_tenants_configured}
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">Test send emails the currently signed-in super admin.</div>
          </div>

          <div className="rounded-xl border border bg-card p-4" data-testid="integ-slack">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium text-foreground">Slack</span>
                <StatusDot ok={s.integrations.slack_tenants_configured > 0} warn={s.integrations.slack_tenants_configured === 0} />
              </div>
              <Button size="sm" onClick={runSlackTest} disabled={testing.slack} className="rounded-lg bg-blue-500 hover:bg-blue-600 text-foreground" data-testid="test-slack-btn">
                {testing.slack ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                Test ping
              </Button>
            </div>
            <div className="text-[11px] text-muted-foreground mt-2">
              Tenants with Slack configured: <span className="text-foreground">{s.integrations.slack_tenants_configured}</span>
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">Test ping uses your (super admin) tenant's Slack settings.</div>
          </div>
        </div>
      </div>

      {/* Activity funnel */}
      <div className="rounded-2xl border border bg-card p-5">
        <SectionHeader icon={TrendingUp} title="Signup Funnel — Last 7 days" />
        <div className="grid grid-cols-3 gap-3" data-testid="funnel-7d">
          <StatCard label="Signups" value={s.activity_7d.signups} icon={Users} tone="info" />
          <StatCard label="Approvals" value={s.activity_7d.approvals} icon={CheckCircle2} tone="good" />
          <StatCard label="Declines" value={s.activity_7d.declines} icon={XCircle} tone={s.activity_7d.declines > 0 ? 'bad' : 'default'} />
        </div>

        {s.recent_actions?.length > 0 && (
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Recent admin actions</div>
            <div className="space-y-1 max-h-60 overflow-auto" data-testid="recent-actions-list">
              {s.recent_actions.map((a, i) => (
                <div key={i} className="flex items-center gap-3 px-3 py-1.5 rounded-lg hover:bg-muted/50 text-xs">
                  <span className="text-muted-foreground w-40 flex-shrink-0">{fmtTime(a.timestamp)}</span>
                  <Badge variant="outline" className="text-[9px] flex-shrink-0">{a.action}</Badge>
                  <span className="text-muted-foreground truncate">{a.resource_id || ''} {typeof a.details === 'object' ? JSON.stringify(a.details) : (a.details || '')}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
